import { createFileRoute, Link } from "@tanstack/react-router";
import { lazy, Suspense, useState } from "react";
import { MapPin } from "lucide-react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { primaryVideoObject } from "@/lib/video-seo";
import { PosterVideo } from "@/components/site/PosterVideo";
import { safeJsonLd } from "@/lib/structured-data-core";
import { absoluteUrl } from "@/lib/absolute-url";

const QuoteForm = lazy(() => import("@/components/site/QuoteForm").then((m) => ({ default: m.QuoteForm })));
const VideoDialog = lazy(() =>
  import("@/components/site/VideoDialog").then((m) => ({ default: m.VideoDialog })),
);

const VIDEO_SRC =
  "https://d17ehmrb6zqtjv.cloudfront.net/Videos/Videos+with+Captions/Restore_BrandMontageStandardFUEWithHarrisBuildup_v1.mp4";

const TITLE = "Hair Sciences Center is Now RESTORE | Dr. James Harris";
const DESCRIPTION =
  "Hair Sciences Center in Denver, Colorado is proud to announce their partnership with the leading hair restoration company, RESTORE Hair.";
const BANNER_IMG = "/img/r/wp-2022-05-hair-sciences-center-banner.webp";
const HARRIS_IMG = "/img/r/wp-2022-01-dr-james2.webp";

export const Route = createFileRoute("/hair-sciences-center")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://restorehair.com/hair-sciences-center/" },
      { property: "og:image", content: absoluteUrl(BANNER_IMG) },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: absoluteUrl(BANNER_IMG) },
    ],
    links: [{ rel: "canonical", href: "https://restorehair.com/hair-sciences-center/" }],
    scripts: [{
      type: "application/ld+json",
      children: safeJsonLd(primaryVideoObject("/hair-sciences-center/")),
    }],
  }),
  component: HairSciencesCenterPage,
});

function HairSciencesCenterPage() {
  const [videoOpen, setVideoOpen] = useState(false);
  return (
    <div className="min-h-screen bg-background font-sans">
      <Header />
      <main>
        <section className="py-12 md:py-16">
          <div className="mx-auto max-w-4xl px-4 text-center lg:px-8">
            <h1 className="text-3xl font-extrabold tracking-tight text-foreground md:text-5xl">
              Hair Sciences Center is Now <span className="text-primary">RESTORE®</span>
            </h1>
            <p className="mt-6 text-base leading-relaxed text-muted-foreground md:text-lg">
              Hair Sciences Center in Denver, Colorado is proud to announce their partnership with
              the leading hair restoration company, RESTORE® Hair.
            </p>
          </div>
          <div className="mx-auto mt-10 max-w-6xl px-4 lg:px-8">
            {/* The <video> lies over the banner so it is in the server HTML
                (Google video indexing); a click still opens the player. */}
            <div className="group relative block w-full overflow-hidden">
              <img
                src={BANNER_IMG}
                alt="Hair Sciences Center is now RESTORE Denver"
                className="block h-auto w-full object-cover transition group-hover:scale-[1.02]"
              />
              <PosterVideo
                src={VIDEO_SRC}
                poster={BANNER_IMG}
                title="Hair Sciences Center Is Now RESTORE Denver"
                fit="cover"
                className="transition group-hover:scale-[1.02]"
              />
              <button
                type="button"
                onClick={() => setVideoOpen(true)}
                aria-label="Play the RESTORE brand montage video"
                className="absolute inset-0 block h-full w-full"
              />
            </div>
          </div>
          <div className="mx-auto mt-10 max-w-3xl px-4 text-center lg:px-8">
            <Link
              to="/locations/restore-denver/"
              className="inline-block bg-primary px-8 py-4 text-sm font-bold uppercase tracking-wide text-primary-foreground transition hover:opacity-90"
            >
              Get a Hair Transplant in Denver
            </Link>
          </div>
        </section>


        <section className="bg-muted py-20 md:py-28">
          <div className="mx-auto grid max-w-7xl grid-cols-1 items-start gap-0 px-4 sm:grid-cols-2 lg:grid-cols-4 lg:px-8">
            <div className="flex h-full items-center bg-background p-6">
              <p className="text-center text-base leading-relaxed text-muted-foreground">
                Hair Sciences Center in Denver is now RESTORE Denver. The same doctors you know and
                trust, with the leading Hair Restoration company.
              </p>
            </div>
            <div className="flex h-full flex-col items-center justify-center bg-muted p-6 text-center">
              <h2 className="text-3xl font-extrabold text-foreground md:text-4xl">RESTORE Denver</h2>
              <Link
                to="/locations/restore-denver/"
                className="mt-6 inline-block bg-primary px-6 py-3 text-sm font-bold uppercase tracking-wide text-primary-foreground transition hover:opacity-90"
              >
                Get a Hair Transplant in Denver
              </Link>
            </div>
            <div className="flex h-full items-center bg-background p-6">
              <p className="text-center text-base leading-relaxed text-muted-foreground">
                Dr. James Harris is a recognized leader and innovator in the field of hair
                restoration. He's restored thousands of patient's hair over his 25 year career.
              </p>
            </div>
            <div className="flex h-full flex-col items-center justify-center bg-muted p-6 text-center">
              <img
                src={HARRIS_IMG}
                alt="Dr. James Harris, Medical Director and Hair Restoration Surgeon in Denver, CO"
                loading="lazy"
                className="mx-auto h-auto w-full max-w-[260px] object-cover"
              />
              <h3 className="mt-4 text-xl font-extrabold text-foreground">Dr. James Harris</h3>
              <p className="text-sm font-bold uppercase tracking-wider text-primary">
                Medical Director &amp; Hair Restoration Surgeon
              </p>
              <div className="mt-1 flex items-center justify-center gap-1 text-sm text-muted-foreground">
                <MapPin className="h-4 w-4" />
                <span>Denver, CO</span>
              </div>
              <Link
                to="/doctors/dr-james-harris/"
                className="mt-4 inline-block border border-primary px-6 py-2 text-sm font-bold uppercase tracking-wide text-primary transition hover:bg-primary hover:text-primary-foreground"
              >
                Read Bio
              </Link>
            </div>
            <div className="flex h-full flex-col items-center justify-center bg-muted p-6 text-center">
              <h2 className="text-3xl font-extrabold text-foreground md:text-4xl">
                Real Clients,
                <br />
                Real Results
              </h2>
              <Link
                to="/results/"
                className="mt-6 inline-block bg-primary px-6 py-3 text-sm font-bold uppercase tracking-wide text-primary-foreground transition hover:opacity-90"
              >
                See Hair Transplant Results
              </Link>
            </div>
            <div className="flex h-full items-center bg-background p-6">
              <p className="text-center text-base leading-relaxed text-muted-foreground">
                Unnatural looking hair plugs, donor area linear scarring &amp; long painful
                recoveries are a thing of the past. The RESTORE F.U.E. Method is a quick, safe and
                comfortable outpatient procedure. The process to get your back takes less than a day.
              </p>
            </div>
            <div className="flex h-full flex-col items-center justify-center bg-muted p-6 text-center">
              <h2 className="text-3xl font-extrabold text-foreground md:text-4xl">
                RESTORE
                <br />
                F.U.E. Method
              </h2>
              <p className="mt-2 text-sm text-muted-foreground">
                *only available in Denver, CO, coming soon to all RESTORE locations
              </p>
              <Link
                to="/why-restore/"
                className="mt-6 inline-block bg-primary px-6 py-3 text-sm font-bold uppercase tracking-wide text-primary-foreground transition hover:opacity-90"
              >
                See If RESTORE FUE Is Right For You
              </Link>
            </div>
            <div className="flex h-full items-center bg-background p-6">
              <p className="text-center text-base leading-relaxed text-muted-foreground">
                Imagine...growing thicker, fuller hair with the revolutionary RESTORE® No-Shave
                FUE™ Method. You&apos;ll be in and out in a day, with little or no downtime. Your hair
                loss won&apos;t stop until you do something about it.
              </p>
            </div>
          </div>
        </section>

        <Suspense fallback={null}>
          <QuoteForm />
        </Suspense>
      </main>
      <Footer />
      {videoOpen && (
        <Suspense fallback={null}>
          <VideoDialog open={videoOpen} onOpenChange={setVideoOpen} src={VIDEO_SRC} />
        </Suspense>
      )}
    </div>
  );
}